import { KeyboardKeys, useKeyboardKeyPressed } from "@core/hooks/use-keyboard-key-pressed";
import { libraryFilterManager } from "@core/service-container";
import { formatDayColumn, getDayColumnIndexes, ProgramContent } from "@domain/program/program";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import styled from "@emotion/styled";
import { AlternativeExercisePanel } from "@ui/pages/program-builder/alternative-exercise/alternative-exercise-panel";
import { DayColumn } from "@ui/pages/program-builder/day-table/day-column";
import { colors } from "@ui/style/colors";
import { row, stack } from "@ui/style/layout";
import { getScrollbarStyle } from "@ui/style/scrollbar";
import { isNotNil } from "@utils/assert";
import { AnimatePresence, motion } from "framer-motion";
import { useComputedObservable, useObservable } from "micro-observables";
import React, { useCallback, useRef, useState } from "react";

interface DayTableProps {
	editedProgram: ProgramContent;
	programEditor: ProgramContentEditor;
	className?: string;
}

export const DAY_GAP = 12;

const OVERLAY_STYLE = {
	hidden: {
		opacity: 0,
	},
	visible: {
		opacity: 1,
		transition: {
			duration: 0.2,
		},
	},
	exit: {
		opacity: 0,
		transition: {
			duration: 0.28,
		},
	},
};

export const DayTable: React.FC<DayTableProps> = ({ editedProgram, programEditor, className }) => {
	const [dayIndexDisabled, setDayIndexDisabled] = useState(-1);
	const [updatedExerciseId, setUpdatedExerciseId] = useState<string | undefined>();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const mainContainerRef = useRef<HTMLDivElement>(null);
	const isSidebarVisible = useObservable(libraryFilterManager.sidebarVisible);

	const exerciseWithAlternativesEdited = useComputedObservable(
		() => libraryFilterManager.exerciseWithAlternativesEdited.get(),
		[libraryFilterManager.exerciseWithAlternativesEdited],
	);

	// dayIndexDisabled is used to disable dropping dragged exercise on days that are not visible
	// (e.g: after scrolling to the right, the first day of the week could be under the exercise library)
	const updateDroppabledDays = () => {
		if (scrollContainerRef.current) {
			const daySize = scrollContainerRef.current.scrollWidth / 7;
			const daysDisabled = Math.round(scrollContainerRef.current.scrollLeft / daySize);
			setDayIndexDisabled(daysDisabled - 1);
		}
	};

	const onAlternativesPanelClose = useCallback(async () => {
		setUpdatedExerciseId(exerciseWithAlternativesEdited?.draggableId);
		programEditor.applyChangesAndResetEditedExercise();
		await libraryFilterManager.clearLibraryFiltersAndSearch();
		// Alternative onboarding step removed; no passStep here
		setTimeout(() => setUpdatedExerciseId(undefined), 1000);
	}, [programEditor, exerciseWithAlternativesEdited?.draggableId]);

	useKeyboardKeyPressed({ [KeyboardKeys.Escape]: () => { onAlternativesPanelClose(); } });

	return (
		<MainContainer ref={mainContainerRef}>
			<OverflowBox ref={scrollContainerRef} onScroll={updateDroppabledDays}>
				<DayTableContainer className={className}>
					{getDayColumnIndexes().map((dayIndex, arrayIndex) => (
						<DayColumn
							key={dayIndex}
							title={formatDayColumn(dayIndex, "long")}
							session={editedProgram.days.get(dayIndex)}
							index={dayIndex}
							isDropDisabled={arrayIndex <= dayIndexDisabled || isNotNil(exerciseWithAlternativesEdited)}
							updatedExerciseId={updatedExerciseId}
							programEditor={programEditor}
						/>
					))}
				</DayTableContainer>
			</OverflowBox>
			<AnimatePresence initial={false} mode="wait">
				{isNotNil(exerciseWithAlternativesEdited) && (
					<Overlay
						style={{
							width: isSidebarVisible ? mainContainerRef.current?.clientWidth : "100%",
							height: mainContainerRef.current?.clientHeight,
						}}
						onClick={onAlternativesPanelClose}
						variants={OVERLAY_STYLE}
						initial="hidden"
						animate="visible"
						exit="exit"
					>
						<AlternativeExercisePanel
							exercise={exerciseWithAlternativesEdited}
							onClose={onAlternativesPanelClose}
							programEditor={programEditor}
							scrollContainerRef={scrollContainerRef}
						/>
					</Overlay>
				)}
			</AnimatePresence>
		</MainContainer>
	);
};

const MainContainer = styled.div`
	${stack(undefined, "flexstart", "stretch")}
	width: 100%;
`;

const DayTableContainer = styled.div`
	${row(DAY_GAP, "flexstart", "stretch")};
	background-color: ${colors.white};
	flex-grow: 1;
	flex: none;

	padding: 20px 12px 16px 12px;

	> * {
		// css for browser supporting -webkit-scrollbar which is not the case of Firefox
		@supports not (-moz-appearance: none) {
			padding-bottom: 12px;
		}
	}
`;

const OverflowBox = styled.div`
	${row(undefined, undefined, "stretch")};
	overflow-x: auto;
	flex: 1 0 auto;

	${getScrollbarStyle(8)};
	// css for browser supporting -webkit-scrollbar which is not the case of Firefox
	@supports not (-moz-appearance: none) {
		margin-bottom: 4px;
	}
`;

const Overlay = styled(motion.div)`
	position: absolute;
	z-index: 11;
	background-color: rgba(0, 0, 0, 0.4);
	overflow-x: hidden;
`;
